import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  formatMoneyForInput,
  isMoneyInputIntermediate,
  parseMoney,
} from '../utils/parsers';

export interface MoneyField2Props {
  label: string;
  value1: number | null | undefined;
  value1Label?: string;
  value2: number | null | undefined;
  value2Label?: string;
  disabled?: boolean;
  allowNegative?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}
function ReadOnlyMoneyInput({
  value,
  valueLabel,
  disabled,
  size,
}: {
  value: number | null | undefined;
  valueLabel?: string;
  disabled: boolean;
  size: 'small' | 'medium';
}): React.ReactElement {
  const [inputStr, setInputStr] = useState(() => formatMoneyForInput(value));

  useEffect(() => {
    setInputStr(formatMoneyForInput(value));
  }, [value]);

  return (
    <TextField
      value={inputStr}
      onChange={() => { }}
      disabled={disabled}
      size={size}
      fullWidth
      inputProps={{ inputMode: 'decimal' }}
      type="text"
      sx={{
        '& .MuiOutlinedInput-root': { pl: 1, pr: 1 },
        '& .MuiOutlinedInput-input': { px: 0.5, textOverflow: 'ellipsis' },
        '& .MuiInputAdornment-root': { '& p': { fontSize: '0.8125rem' } },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">$</InputAdornment>
        ),
        endAdornment: valueLabel ? (
          <InputAdornment position="end">{valueLabel}</InputAdornment>
        ) : undefined,
      }}
    />
  );
}
export function MoneyField2({
  label,
  value1,
  value1Label,
  value2,
  value2Label,
  disabled = false,
  size = 'small',
  fullWidth = true,
}: MoneyField2Props): React.ReactElement {

  const containerRef = useRef<HTMLDivElement>(null);
  const measure1Ref = useRef<HTMLSpanElement>(null);
  const measure2Ref = useRef<HTMLSpanElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [minItemWidth, setMinItemWidth] = useState(120);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const ADORNMENT_PX = 48; // "$" adornment + input padding + borders
    const w1 = measure1Ref.current?.offsetWidth ?? 0;
    const w2 = measure2Ref.current?.offsetWidth ?? 0;
    setMinItemWidth(Math.max(120, w1 + ADORNMENT_PX, w2 + ADORNMENT_PX));
  }, [value1, value2, value1Label, value2Label]);

  // 2 inputs at full required width + 1px divider + 2×8px gaps
  const stacked = containerWidth > 0 && containerWidth < minItemWidth * 2 + 17;

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, width: fullWidth ? '100%' : undefined }}>
      <Box aria-hidden sx={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', whiteSpace: 'nowrap', fontSize: '1rem' }}>
        <span ref={measure1Ref}>{formatMoneyForInput(value1)}{value1Label ? ` (${value1Label})` : ''}</span>
        <span ref={measure2Ref}>{formatMoneyForInput(value2)}{value2Label ? ` (${value2Label})` : ''}</span>
      </Box>

      <Box sx={{ flex: stacked ? '1 1 100%' : `1 1 ${minItemWidth}px`, minWidth: stacked ? undefined : minItemWidth }}>
        <ReadOnlyMoneyInput
          value={value1}
          valueLabel={value1Label ? `(${value1Label})` : undefined}
          disabled={disabled}
          size={size}
        />
      </Box>
      <Box sx={{
        flexShrink: 0, height: 32, width: '1px', bgcolor: 'divider', alignSelf: 'center',
        display: stacked ? 'none' : 'block'
      }} />
      <Box sx={{ flex: stacked ? '1 1 100%' : `1 1 ${minItemWidth}px`, minWidth: stacked ? undefined : minItemWidth }}>
        <ReadOnlyMoneyInput
          value={value2}
          valueLabel={value2Label ? `(${value2Label})` : undefined}
          disabled={disabled}
          size={size}
        />
      </Box>
    </Box>
  );
}
